import { useMutation, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { httpClient } from "../network";
import {
  getScheduleExpiryNotificationsEndpoint,
  getScheduleExpiryNotificationsCountEndpoint,
  dismissScheduleExpiryNotificationEndpoint,
  optOutScheduleExpiryNotificationEndpoint,
  scheduleExpiryNotificationsSettingsEndpoint,
} from "../endpoints/scheduleExpiryNotificationEndpoints";
import { toast } from "react-toastify";

export type ScheduleExpirySeverity = "INFO" | "WARNING" | "HIGH" | "CRITICAL";

export interface ScheduleExpiryNotificationItem {
  id: string;
  threshold: number;
  severity: ScheduleExpirySeverity;
  createdAt: string;
}

export interface ScheduleExpiryNotificationGroup {
  staffId: string;
  staffName: string;
  expiryDate: string;
  highestSeverity: ScheduleExpirySeverity;
  notifications: ScheduleExpiryNotificationItem[];
}

export interface ScheduleExpiryNotificationsResponse {
  status: number;
  data: ScheduleExpiryNotificationGroup[];
  meta: {
    totalActive: number;
    hasUrgent: boolean;
  };
}

export interface ScheduleExpiryNotificationsCountResponse {
  status: number;
  data: {
    total: number;
    critical: number;
    high: number;
    warning: number;
    info: number;
  };
}

export const useGetScheduleExpiryNotifications = (enabled: boolean = true): UseQueryResult<ScheduleExpiryNotificationsResponse> => {
  return useQuery({
    queryKey: ["scheduleExpiryNotifications"],
    queryFn: async () => {
      const response: AxiosResponse<ScheduleExpiryNotificationsResponse> = await httpClient.get(
        getScheduleExpiryNotificationsEndpoint()
      );
      return response.data;
    },
    enabled,
    retry: 0,
    refetchInterval: 5 * 60 * 1000, // Polling svakih 5 minuta
  });
};

export const useGetScheduleExpiryNotificationsCount = (enabled: boolean = true): UseQueryResult<ScheduleExpiryNotificationsCountResponse> => {
  return useQuery({
    queryKey: ["scheduleExpiryNotificationsCount"],
    queryFn: async () => {
      const response: AxiosResponse<ScheduleExpiryNotificationsCountResponse> = await httpClient.get(
        getScheduleExpiryNotificationsCountEndpoint()
      );
      return response.data;
    },
    enabled,
    retry: 0,
    refetchInterval: 5 * 60 * 1000,
  });
};

export const useDismissScheduleExpiryNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response: AxiosResponse = await httpClient.patch(
        dismissScheduleExpiryNotificationEndpoint(notificationId)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduleExpiryNotifications"] });
      queryClient.invalidateQueries({ queryKey: ["scheduleExpiryNotificationsCount"] });
    },
    onError: () => {
      toast.error("Greška prilikom označavanja notifikacije kao pročitane.");
    },
  });
};

export const useOptOutScheduleExpiryNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (staffId: string) => {
      const response: AxiosResponse = await httpClient.patch(
        optOutScheduleExpiryNotificationEndpoint(staffId)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduleExpiryNotifications"] });
      queryClient.invalidateQueries({ queryKey: ["scheduleExpiryNotificationsCount"] });
      toast.success("Notifikacije za ovog zaposlenika su ugašene do sljedećeg generisanja radnog vremena.");
    },
    onError: () => {
      toast.error("Greška prilikom isključivanja notifikacija.");
    },
  });
};

export const useUpdateScheduleExpiryNotificationsSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (scheduleExpiryNotifications: boolean) => {
      const response: AxiosResponse = await httpClient.post(
        scheduleExpiryNotificationsSettingsEndpoint(),
        { scheduleExpiryNotifications }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduleExpiryNotifications"] });
      queryClient.invalidateQueries({ queryKey: ["scheduleExpiryNotificationsCount"] });
      queryClient.invalidateQueries({ queryKey: ["getAllServices"] });
      toast.success("Postavke notifikacija su spašene.");
    },
    onError: () => {
      toast.error("Greška prilikom spremanja postavki notifikacija.");
    },
  });
};
